import { Banner } from '../../core/models/banner.model';
import { PaymentStatus } from '../../pay/models/payment-status.model';
import { BasePayment } from '../../pay/models/payment.model';
import { StatusType } from '../../shared/models/status-type.enum';
import { Liability } from './liability.model';

export class Liabilities {
  public unpaidIds: string[];
  public months: LiabilityMonth[];
  public hasBillers: boolean;
  public showPhoneBanner: boolean;
}

export class LiabilityMonth {
  public month: string;
  public year: string;
  public liabilities: Liability[];
  public current?: boolean;
}

export class LiabilitiesDashboard {
  public liabilities: Liability[];
  public allLiabilitiesCount: number;
  public unpaidIds: string[];
  public hasBillers: boolean;
  public banner: Banner;
  public unpaidAmount: number;
  public unpaidCount: number;
}

export class UrgentLiabilities {
  public liabilities: Liability[];
  public hasBillers: boolean;
  public uncompletedPayment?: BasePayment;
}

export interface ILiabilityPayment {
  id: string;
  paidAt: Date;
  amount: number;
  commision: number;
  hasConfirmation: boolean;
  confirmationLoading: boolean;
  status: PaymentStatus;
}

export class LiabilityDocument {
  public id: string;
}

export enum LiabilityStatus {
  NOT_PAID = 'NOT_PAID',
  OVERDUE = 'OVERDUE',
  PAID_ON_PLATFORM = 'PAID_ON_PLATFORM',
  PAID_OUTSIDE_PLATFORM = 'PAID_OUTSIDE_PLATFORM',
  PENDING_PAYMENT = 'PENDING_PAYMENT',
  IN_CART = 'IN_CART',
  UNKNOWN = 'UNKNOWN',
  SCHEDULED_NOT_PAID = 'SCHEDULED_NOT_PAID',
  SCHEDULED_PENDING = 'SCHEDULED_PENDING',
  SCHEDULED_AUTO_PAID = 'SCHEDULED_AUTO_PAID',
  DELETED = 'DELETED',
  IN_VERIFICATION = 'IN_VERIFICATION',
  NOT_VERIFIED = 'NOT_VERIFIED'
}

export interface LiabilityStatusFilter {
  name: LiabilityStatusFilterValue;
  description: string;
  filterDescription: string;
  statuses: LiabilityStatus[];
}

export enum LiabilityStatusFilterValue {
  PAID = 'PAID',
  OVERDUE = 'OVERDUE',
  NOT_PAID = 'NOT_PAID'
}


export type CouponRedemptionStatus =
  | 'REDEEMED'
  | 'NO_VERIFIED_BILLERS'
  | 'TITLE_BLACKLISTED'
  | 'NOT_SELECTED_COUPON'
  | 'NOT_VERIFIED_CUSTOMER'
  | 'BAD_SOURCE'
  | 'LIABILITY_ALREADY_REDEEMED';

export class ImojeCall {
  constructor(readonly apiUrl: string, readonly paymentRequestParams: { [key: string]: string }) {
  }
}

export class Transaction {
  public transactionId: string;
  public title: string;
  public amount: number;
  public refundedAmount: number;
}

export const statusMap: { [key: string]: IStatusDefinition } = {
  NOT_PAID: {
    description: 'Przed terminem',
    icon: 'alarm',
    tooltip: 'Termin płatności jeszcze nie minął',
    type: StatusType.NEUTRAL,
  },
  OVERDUE: {
    description: 'Po terminie',
    icon: 'notification_important',
    tooltip: 'Termin płatności minął',
    type: StatusType.ERROR,
  },
  PAID_ON_PLATFORM: {
    description: 'Rozliczony',
    icon: 'done_all',
    tooltip: 'Rozliczony na platformie',
    type: StatusType.SUCCESS,
  },
  PAID_OUTSIDE_PLATFORM: {
    description: 'Rozliczony',
    icon: 'done',
    tooltip: 'Rozliczony poza platformą',
    type: StatusType.SUCCESS,
  },
  PENDING_PAYMENT: {
    description: 'Rozpoczęto',
    icon: 'sync',
    tooltip: 'Rozpoczęto płatność',
    type: StatusType.PENDING,
  },
  PENDING_REFUND: {
    description: 'Zwrot w realizacji',
    icon: 'sync',
    tooltip: '',
    type: StatusType.PENDING,
  },
  SCHEDULED_AUTO_PAID: {
    description: 'Rozliczony',
    icon: 'alarm_on',
    tooltip: 'Rozliczony automatycznie na platformie',
    type: StatusType.SUCCESS,
  },
  SCHEDULED_NOT_PAID: {
    description: 'Zaplanowana',
    icon: 'schedule_send',
    tooltip: 'Płatność automatyczna zaplanowana',
    type: StatusType.SUCCESS,
  },
  SCHEDULED_PENDING: {
    description: 'Rozpoczęto',
    icon: 'sync',
    tooltip: 'Rozpoczęto płatność',
    type: StatusType.PENDING,
  },
  UNKNOWN: {
    description: 'Brak informacji',
    icon: 'sync_problem',
    tooltip: 'Status płatności tej faktury jest nieznany',
    type: StatusType.NEUTRAL,
  },
  DELETED: {
    description: 'Usunięta',
    icon: 'close',
    tooltip: 'Opłata została usunięta',
    type: StatusType.NEUTRAL,
  },
  IN_VERIFICATION: {
    description: 'W weryfikacji',
    icon: 'close',
    tooltip: 'Opłata jest w weryfikacji',
    type: StatusType.NEUTRAL,
  },
  NOT_VERIFIED: {
    description: 'Odrzucony',
    icon: 'close',
    tooltip: 'Numer rachunku bądź dostawca nie został zweryfikowany pozytywnie',
    type: StatusType.NEUTRAL,
  },
  IN_CART: {
    description: 'W koszyku',
    icon: 'close',
    tooltip: 'W koszyku',
    type: StatusType.NEUTRAL,
  },
};

export interface IStatusDefinition {
  description: string;
  type: StatusType;
  tooltip: string;
  icon: string;
}

export interface IErrorVM {
  errorCode: string;
  errorData: {
    billers: string[];
    titles: string[];
    liabilities: IRefuseCreatePaymentLiabilityVM[];
    customerBillerDiplayName: string;
    subAccountDisplayName: string;
  };
}

export interface IRefuseCreatePaymentLiabilityVM {
  liabilityId: string;
  billerDescription: string;
  paymentDue: Date;
  amount: number;
}

export interface ICustomerBillersInfo {
  billers: string;
  inboxEmailAddress: string;
}

export interface INewCouponSupportedBiller {
  customerBillerId: string;
  liabilitySource: string;
  resultType: NewCouponSupportedBillerResultType;
}

export type NewCouponSupportedBillerResultType = 'CREATED' | 'NO_SUPPORTED_BILLERS';

export enum LiabilityMenuDisabledState {
  DISABLED = 'DISABLED',
  ENABLED = 'ENABLED',
}

export function getStatusDescription(status: LiabilityStatus): string {
  const statusDefinition = statusMap[status];
  return statusDefinition ? statusDefinition.description : 'Brak opisu';
}

export function getStatusHtml(status: LiabilityStatus): string {
  return `<div style="font-weight: bold">${getStatusDescription(status)}</div>`;
}

export function getIcon(status: LiabilityStatus): string {
  const statusDefinition = statusMap[status];
  return statusDefinition ? statusDefinition.icon : '';
}
